/**
 * Downscale + re-encode an image File before base64-ing it for upload.
 *
 * Why: phones routinely produce 5-12MB JPEGs. Storing those in MongoDB as
 * base64 is wasteful and slow to load. We resize to MAX_PHOTO_DIMENSION on
 * the longest edge and re-encode as JPEG at PHOTO_JPEG_QUALITY — usually
 * lands the final payload around 200-500KB without visible quality loss.
 *
 * Falls back to the raw FileReader path if the browser doesn't support
 * canvas (very old browsers).
 */
import { MAX_PHOTO_DIMENSION, PHOTO_JPEG_QUALITY } from '../constants';

const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error || new Error('read failed'));
  reader.readAsDataURL(file);
});

const loadImage = (dataUrl) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('image load failed'));
  img.src = dataUrl;
});

export const compressImage = async (file) => {
  // Skip resize for tiny images — no point burning a render
  if (file.size < 300 * 1024) {
    return readFileAsDataURL(file);
  }
  try {
    const dataUrl = await readFileAsDataURL(file);
    const img = await loadImage(dataUrl);
    let { width, height } = img;
    const longest = Math.max(width, height);
    if (longest > MAX_PHOTO_DIMENSION) {
      const scale = MAX_PHOTO_DIMENSION / longest;
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;  // browser too old, fall back to raw
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', PHOTO_JPEG_QUALITY);
  } catch (err) {
    // If anything in the canvas pipeline failed, fall back to the raw file
    return readFileAsDataURL(file);
  }
};
